import React from 'react';
import { Camera } from 'lucide-react';

interface ProfileHeroPhotoEmptyProps {
  userName?: string;
  onEdit?: () => void;
  className?: string;
}

/**
 * V277.9: Beautiful empty photo placeholder - WORKING on both web and Android
 * - Always shows "Add photo" button
 * - WebkitFilter for Safari compatibility
 * - Purple gradient + blur orbs + sparkles
 * - Debug logging to track rendering
 */
const ProfileHeroPhotoEmpty: React.FC<ProfileHeroPhotoEmptyProps> = ({
  userName,
  onEdit,
  className = '',
}) => {
  // V277.9: Debug logging
  console.log('🎨 V277.9: ProfileHeroPhotoEmpty RENDERING:', {
    userName,
    hasOnEdit: !!onEdit,
    onEditType: typeof onEdit,
  });

  const initial = userName?.charAt(0)?.toUpperCase() || 'U';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      console.log('✅ V277.9: Calling onEdit handler');
      onEdit();
    } else {
      console.warn('⚠️ V277.9: onEdit not provided, dispatching photoEditRequest event');
      window.dispatchEvent(new CustomEvent('photoEditRequest', { detail: { userName } }));
    }
  };

  return (
    <div
      className={className}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick(e as any)}
      aria-label="Add profile photo"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        borderRadius: 'inherit',
        overflow: 'hidden',
        cursor: 'pointer',
        // V277.9: Beautiful purple gradient - NOT green!
        background: 'linear-gradient(145deg, #7c3aed 0%, #6366f1 35%, #4f46e5 65%, #7c3aed 100%)',
      }}
    >
      {/* Gradient overlay for depth */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, transparent 30%, rgba(0,0,0,0.2) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Pink blur orb - top left */}
      <div
        style={{
          position: 'absolute',
          top: '-15%',
          left: '-15%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'rgba(236, 72, 153, 0.35)',
          filter: 'blur(50px)',
          WebkitFilter: 'blur(50px)', // V277.9: Safari compatibility
          pointerEvents: 'none',
        }}
      />

      {/* Blue blur orb - bottom right */}
      <div
        style={{
          position: 'absolute',
          bottom: '-15%',
          right: '-15%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.35)',
          filter: 'blur(50px)',
          WebkitFilter: 'blur(50px)', // V277.9: Safari compatibility
          pointerEvents: 'none',
        }}
      />

      {/* Purple center glow */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(139, 92, 246, 0.25)',
          filter: 'blur(40px)',
          WebkitFilter: 'blur(40px)', // V277.9: Safari compatibility
          pointerEvents: 'none',
        }}
      />

      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
        }}
      >
        {/* Large initial letter */}
        <div
          style={{
            fontSize: '96px',
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.95)',
            textShadow: '0 4px 30px rgba(0, 0, 0, 0.4), 0 2px 10px rgba(0, 0, 0, 0.2)',
            lineHeight: 1,
            letterSpacing: '0.02em',
          }}
        >
          {initial}
        </div>

        {/* V277.9: ALWAYS show Add photo button (removed conditional) */}
        <button
          type="button"
          onClick={handleClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            borderRadius: '9999px',
            background: 'rgba(255, 255, 255, 0.18)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.28)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Camera size={20} strokeWidth={2.5} />
          <span>Add photo</span>
        </button>
      </div>

      {/* Decorative sparkle - top right (yellow) */}
      <div
        style={{
          position: 'absolute',
          top: '28px',
          right: '28px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'rgba(253, 224, 71, 0.9)',
          boxShadow: '0 0 12px 2px rgba(253, 224, 71, 0.6)',
          zIndex: 15,
        }}
      />

      {/* Decorative sparkle - bottom left (pink) */}
      <div
        style={{
          position: 'absolute',
          bottom: '80px',
          left: '28px',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: 'rgba(244, 114, 182, 0.9)',
          boxShadow: '0 0 10px 2px rgba(244, 114, 182, 0.5)',
          zIndex: 15,
        }}
      />

      {/* Decorative sparkle - middle right (white) */}
      <div
        style={{
          position: 'absolute',
          top: '35%',
          right: '24px',
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.8)',
          boxShadow: '0 0 8px 1px rgba(255, 255, 255, 0.4)',
          zIndex: 15,
        }}
      />

      {/* Inner border ring for polish */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.15)',
          pointerEvents: 'none',
          zIndex: 20,
        }}
      />
    </div>
  );
};

export default ProfileHeroPhotoEmpty;

